// AdminImportExistingStudents.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Chip,
  DialogActions,
  Dialog as MuiDialog,
  LinearProgress,
  Divider,
  Tabs,
  Tab,
  Grid,
  Paper,
  Checkbox,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ListIcon from '@mui/icons-material/List';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PeopleIcon from '@mui/icons-material/People';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import api from '../../../API/axiosInstance';

ModuleRegistry.registerModules([AllCommunityModule]);
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const AdminImportExistingStudents = ({ open, onClose }) => {
  const [currentTab, setCurrentTab] = useState(0);
  
  // Upload tab states
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [sheetName, setSheetName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [importedRecords, setImportedRecords] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Uploaded Details tab states
  const gridRef = useRef(null);
  const [uploadedStudents, setUploadedStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedUploadIds, setSelectedUploadIds] = useState([]);
  const [movingStudents, setMovingStudents] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const resetAll = () => {
    setFile(null);
    setParsing(false);
    setSheetName('');
    setHeaders([]);
    setRows([]);
    setIsDownloading(false);
    setIsUploading(false);
    setConfirmOpen(false);
    setUploadStatus(null);
    setResponseMessage('');
    setImportedRecords(0);
    setErrorMessage('');
  };

  const handleCloseDialog = () => {
    resetAll();
    setCurrentTab(0);
    setUploadedStudents([]);
    setSelectedUploadIds([]);
    onClose();
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    if (newValue === 1) {
      fetchUploadedStudents();
    }
  };

  const fetchUploadedStudents = async () => {
    setLoadingStudents(true);
    try {
      const response = await api.get('/StudentRequest/get-all-existing-students?isActive=true&movedToAlumni=false');
      
      // Convert PascalCase to camelCase
      const convertedData = (response.data || []).map((item) => ({
        uploadId: item.uploadId || item.UploadId,
        erpStudentId: item.erpStudentId || item.ErpStudentId,
        rollNum: item.rollNum || item.RollNum,
        regNum: item.regNum || item.RegNum,
        firstName: item.firstName || item.FirstName,
        lastName: item.lastName || item.LastName,
        email: item.email || item.Email,
        mobile: item.mobile || item.Mobile,
        whatsAppNumber: item.whatsAppNumber || item.WhatsAppNumber,
        batchYear: item.batchYear || item.BatchYear,
        dateOfBirth: item.dateOfBirth || item.DateOfBirth,
        uploadedDate: item.uploadedDate || item.UploadedDate,
        movedToAlumni: item.movedToAlumni ?? item.MovedToAlumni,
        movedDate: item.movedDate || item.MovedDate,
        movedBy: item.movedBy || item.MovedBy,
        movedByName: item.movedByName || item.MovedByName,
        uploadedByName: item.uploadedByName || item.UploadedByName,
        instype: item.instype || item.INSTYPE,
        intname: item.intname || item.INTNAME,
        cat: item.cat || item.CAT,
        dept: item.dept || item.DEPT,
        deg: item.deg || item.DEG,
        coursename: item.coursename || item.COURSENAME,
        gender: item.gender || item.GENDER,
        community: item.community || item.COMMUNITY,
        bloodgroup: item.bloodgroup || item.BLOODGROUP,
        country: item.country || item.COUNTRY,
        state: item.state || item.STATE,
        district: item.district || item.DISTRICT,
      }));
      
      setUploadedStudents(convertedData);
    } catch (error) {
      console.error('Error fetching existing students:', error);
      setSnackbar({ open: true, message: 'Failed to fetch existing students', severity: 'error' });
    } finally {
      setLoadingStudents(false);
    }
  };

  const checkboxCellRenderer = (params) => {
    if (!params.data) return null;
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '4px' }}>
        <Checkbox
          size="small"
          checked={selectedUploadIds.includes(params.data.uploadId)}
          onChange={(e) => {
            const uploadId = params.data.uploadId;
            if (e.target.checked) {
              setSelectedUploadIds((prev) => [...prev, uploadId]);
            } else {
              setSelectedUploadIds((prev) => prev.filter((id) => id !== uploadId));
            }
          }}
          sx={{ padding: '0' }}
        />
      </Box>
    );
  };

  const statusCellRenderer = (params) => {
    const moved = params.data?.movedToAlumni;
    return (
      <Chip
        label={moved ? 'Moved' : 'Pending'}
        color={moved ? 'success' : 'warning'}
        size="small"
        sx={{ fontSize: '11px', height: '24px' }}
      />
    );
  };

  const columnDefs = useMemo(
    () => [
      {
        field: 'checkbox',
        headerName: '',
        width: 50,
        cellRenderer: checkboxCellRenderer,
        checkboxSelection: false,
        headerCheckboxSelection: false,
        resizable: false,
        lockPosition: true,
      },
      { field: 'erpStudentId', headerName: 'ERP ID', width: 100, filter: true, sortable: true },
      { field: 'rollNum', headerName: 'Roll #', width: 100, filter: true, sortable: true },
      { field: 'regNum', headerName: 'Reg #', width: 100, filter: true, sortable: true },
      { field: 'firstName', headerName: 'First Name', width: 140, filter: true, sortable: true },
      { field: 'lastName', headerName: 'Last Name', width: 140, filter: true, sortable: true },
      { field: 'email', headerName: 'Email', width: 180, filter: true, sortable: true },
      { field: 'mobile', headerName: 'Mobile', width: 120, filter: true, sortable: true },
      { field: 'batchYear', headerName: 'Batch', width: 80, filter: true, sortable: true },
      { field: 'instype', headerName: 'Inst Type', width: 120, filter: true, sortable: true },
      { field: 'intname', headerName: 'Institute', width: 140, filter: true, sortable: true },
      { field: 'dept', headerName: 'Department', width: 140, filter: true, sortable: true },
      { field: 'deg', headerName: 'Degree', width: 120, filter: true, sortable: true },
      { field: 'coursename', headerName: 'Course', width: 140, filter: true, sortable: true },
      { field: 'gender', headerName: 'Gender', width: 90, filter: true, sortable: true },
      { field: 'country', headerName: 'Country', width: 120, filter: true, sortable: true },
      { field: 'state', headerName: 'State', width: 120, filter: true, sortable: true },
      {
        field: 'uploadedDate',
        headerName: 'Uploaded Date',
        width: 130,
        valueFormatter: (params) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleDateString();
        },
        filter: true,
        sortable: true,
      },
      {
        field: 'movedToAlumni',
        headerName: 'Status',
        width: 100,
        cellRenderer: statusCellRenderer,
        filter: true,
        sortable: true,
      },
    ],
    [statusCellRenderer, checkboxCellRenderer]
  );

  const handleFileChange = async (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;

    setFile(selected);
    await parseExcel(selected);
  };

  const parseExcel = async (selectedFile) => {
    setParsing(true);
    setHeaders([]);
    setRows([]);
    setErrorMessage('');
    try {
      const XLSX = await import('xlsx');
      const data = await selectedFile.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });

      const wsName = wb.SheetNames[0];
      const ws = wb.Sheets[wsName];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });

      const hdrs = json.length ? Object.keys(json[0]) : [];

      setSheetName(wsName);
      setHeaders(hdrs);
      setRows(json);
    } catch (err) {
      console.error('Excel parse error:', err);
      setErrorMessage('Failed to parse the Excel file. Please ensure it is a valid .xlsx or .xls file.');
    } finally {
      setParsing(false);
    }
  };

  const downloadTemplate = async () => {
    setIsDownloading(true);
    try {
      const response = await api.get('/StudentRequest/download-existing-students-template', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'ExistingStudentsTemplate.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      setErrorMessage('Failed to download the template. Please try again later.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleAskConfirm = () => {
    if (!file || rows.length === 0) {
      setErrorMessage('Please upload a valid Excel file with at least one data row before importing.');
      return;
    }
    setConfirmOpen(true);
  };

  const handleImport = async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadStatus(null);
    setErrorMessage('');
    setConfirmOpen(false);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(
        `/StudentRequest/upload-existing-students?uploadedByUserId=1`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      const result = response.data;
      setResponseMessage(result.message || 'Imported successfully.');
      setImportedRecords(result.insertedCount || rows.length);
      setUploadStatus('success');
      
      // Refresh uploaded students list if on details tab
      if (currentTab === 1) {
        fetchUploadedStudents();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to upload the file. Please try again.');
      setUploadStatus('error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleMoveStudents = async () => {
    if (selectedUploadIds.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one student to move', severity: 'warning' });
      return;
    }

    setMovingStudents(true);
    try {
      const response = await api.post('/StudentRequest/move-students-to-alumni', {
        UploadIds: selectedUploadIds,
        MovedBy: 1, // Replace with actual user ID
      });

      const result = response.data;
      setSnackbar({
        open: true,
        message: result.resultMessage || `Successfully moved ${result.successCount} student(s) to alumni portal.`,
        severity: result.errorCount > 0 ? 'warning' : 'success',
      });

      // Refresh the list
      await fetchUploadedStudents();
      setSelectedUploadIds([]);
    } catch (error) {
      console.error('Error moving students:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to move students to alumni portal.',
        severity: 'error',
      });
    } finally {
      setMovingStudents(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseDialog} fullScreen>
        {isUploading && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 3 }} />}

        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
          <Tooltip title="Close">
            <IconButton onClick={handleCloseDialog} color="error">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <DialogTitle sx={{ pr: 8, fontWeight: 'bold', color: 'primary.main' }}>
          Import Existing Students
        </DialogTitle>

        <DialogContent sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', pt: 2 }}>
          {/* Tabs */}
          <Box sx={{ mb: 3 }}>
            <Tabs value={currentTab} onChange={handleTabChange} variant="fullWidth">
              <Tab
                icon={<CloudUploadIcon />}
                iconPosition="start"
                label="Upload"
                sx={{ textTransform: 'none' }}
              />
              <Tab
                icon={<ListIcon />}
                iconPosition="start"
                label="Uploaded Details"
                sx={{ textTransform: 'none' }}
              />
            </Tabs>
          </Box>

          <MDBox display="flex" flexDirection="column" gap={2}>
            {/* Upload Tab Content */}
            {currentTab === 0 && (
              <>
                {/* SUCCESS */}
                {uploadStatus === 'success' && (
                  <Card sx={{ p: 2 }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 1 }} />
                      <MDTypography variant="h5" gutterBottom>
                        Upload Successful
                      </MDTypography>
                      <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {responseMessage || 'Uploaded successfully.'}
                      </MDTypography>
                      <Chip label={`Records imported: ${importedRecords}`} color="success" variant="outlined" sx={{ mb: 2 }} />
                      <Box>
                        <MDButton color="primary" onClick={resetAll}>
                          Upload Another File
                        </MDButton>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* ERROR */}
                {uploadStatus === 'error' && (
                  <Card sx={{ p: 2 }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <ErrorIcon color="error" sx={{ fontSize: 64, mb: 1 }} />
                      <MDTypography variant="h5" gutterBottom>
                        Upload Failed
                      </MDTypography>
                      <MDTypography variant="body2" color="error" sx={{ mb: 2 }}>
                        {errorMessage || 'Failed to upload the file.'}
                      </MDTypography>
                      <Box>
                        <MDButton color="primary" onClick={resetAll}>
                          Try Again
                        </MDButton>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* UPLOAD FORM */}
                {uploadStatus !== 'success' && uploadStatus !== 'error' && (
                  <Card>
                    <CardContent>
                      <MDTypography variant="h6" gutterBottom>
                        Import Existing Students
                      </MDTypography>
                      <MDTypography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Download the template, fill in the student details, and upload the Excel file.
                      </MDTypography>

                      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <MDButton
                          variant="contained"
                          color="primary"
                          startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                          onClick={downloadTemplate}
                          disabled={isDownloading}
                          sx={{ minWidth: 200 }}
                        >
                          {isDownloading ? 'Downloading...' : 'Download Template'}
                        </MDButton>

                        {!file && (
                          <>
                            <input
                              accept=".xlsx,.xls"
                              style={{ display: 'none' }}
                              id="file-upload"
                              type="file"
                              onChange={handleFileChange}
                            />
                            <label htmlFor="file-upload">
                              <MDButton variant="contained" component="span" startIcon={<UploadFileIcon />}>
                                Choose File
                              </MDButton>
                            </label>
                          </>
                        )}

                        {file && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <MDTypography variant="body2" color="success.main" fontWeight="medium">
                              File selected: {file.name}
                            </MDTypography>
                            <MDButton
                              variant="outlined"
                              size="small"
                              color="error"
                              onClick={() => {
                                setFile(null);
                                setRows([]);
                                setHeaders([]);
                                setErrorMessage('');
                              }}
                            >
                              Remove
                            </MDButton>
                          </Box>
                        )}
                      </Box>

                      {parsing && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <CircularProgress size={24} />
                          <MDTypography variant="body2" color="text.secondary">
                            Parsing file...
                          </MDTypography>
                        </Box>
                      )}

                      {errorMessage && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {errorMessage}
                        </Alert>
                      )}

                      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                        <MDButton
                          variant="contained"
                          color="success"
                          onClick={handleAskConfirm}
                          disabled={!file || rows.length === 0 || parsing || isUploading}
                        >
                          Import
                        </MDButton>
                        <MDButton variant="outlined" onClick={resetAll}>
                          Reset
                        </MDButton>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Uploaded Details Tab Content */}
            {currentTab === 1 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <MDTypography variant="h6">Uploaded Existing Students</MDTypography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <MDButton
                      variant="contained"
                      color="success"
                      startIcon={<MoveToInboxIcon />}
                      onClick={handleMoveStudents}
                      disabled={selectedUploadIds.length === 0 || movingStudents}
                      size="small"
                    >
                      {movingStudents ? 'Moving...' : `Move Selected (${selectedUploadIds.length})`}
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={fetchUploadedStudents}
                      disabled={loadingStudents}
                      size="small"
                    >
                      Refresh
                    </MDButton>
                  </Box>
                </Box>

                {loadingStudents ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : uploadedStudents.length === 0 ? (
                  <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <MDTypography variant="body1" color="text.secondary">
                      No existing students uploaded yet.
                    </MDTypography>
                  </Paper>
                ) : (
                  <Box
                    className="ag-theme-alpine"
                    sx={{
                      height: '70vh',
                      width: '100%',
                      '& .ag-header-cell-label': { fontSize: '11px', fontWeight: 'bold' },
                      '& .ag-cell': { fontSize: '11px', padding: '4px 8px' },
                    }}
                  >
                    <AgGridReact
                      ref={gridRef}
                      rowData={uploadedStudents}
                      columnDefs={columnDefs}
                      rowHeight={35}
                      headerHeight={32}
                      defaultColDef={{
                        resizable: true,
                        sortable: true,
                        filter: true,
                      }}
                      suppressSizeToFit={true}
                      rowSelection="multiple"
                      onGridReady={(params) => {
                        params.api.sizeColumnsToFit();
                      }}
                      onFirstDataRendered={(params) => {
                        params.api.sizeColumnsToFit();
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </MDBox>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <MuiDialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Import</DialogTitle>
        <DialogContent>
          <MDTypography>
            Are you sure you want to import {rows.length} student record(s)?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setConfirmOpen(false)}>Cancel</MDButton>
          <MDButton onClick={handleImport} color="success" variant="contained">
            Confirm
          </MDButton>
        </DialogActions>
      </MuiDialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </>
  );
};

AdminImportExistingStudents.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminImportExistingStudents;

